import { Children, isValidElement, ReactNode } from 'react';
import { Popover } from '@headlessui/react';
import VerticalNavTab from './VerticalNavTab';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';

type CollapsedTabsListProps = {
    children: ReactNode[] | ReactNode;
    pill: boolean;
    colorMode: 'dark' | 'light';
    childOffsetIndex: number;
    tabItemsCount: number;
    isDisabled: boolean;
};

export default function CollapsedTabsList({
    children,
    childOffsetIndex,
    tabItemsCount,
    pill,
    isDisabled,
    colorMode,
}: CollapsedTabsListProps): JSX.Element {
    const styles = {
        tab: {
            default: {
                unselected: `${
                    colorMode === 'light' ? 'text-gray-500 dark:text-gray-400' : 'text-white dark:text-white'
                } whitespace-nowrap py-2 font-medium text-sm cursor-pointer group inline-flex items-center cursor:pointer outline-none px-3 mx-1`,
                selected: `${colorMode === 'light' ? 'text-indigo-600 dark:text-indigo-500' : 'text-white dark:text-white'}`,
                disabled: 'opacity-75 cursor-not-allowed',
            },
            pill: {
                unselected: `${
                    colorMode === 'light' ? 'text-gray-500 dark:text-gray-400' : 'text-white dark:text-white'
                } whitespace-nowrap py-2 font-medium text-sm cursor-pointer group inline-flex items-center cursor:pointer outline-none px-4`,
                selected: `${
                    colorMode === 'light'
                        ? 'text-indigo-700 dark:text-indigo-100 bg-indigo-50 dark:bg-indigo-900'
                        : 'text-white dark:text-white bg-indigo-800 dark:bg-indigo-800'
                }  rounded-md py-3 `,
                disabled: 'opacity-75 cursor-not-allowed',
            },
        },
    };

    return (
        <Popover>
            {({ open, close }): JSX.Element => (
                <>
                    <Popover.Button
                        className={`${pill ? styles.tab.pill.unselected : styles.tab.default.unselected} ${
                            open ? (pill ? styles.tab.pill.selected : styles.tab.default.selected) : null
                        } ${isDisabled && (pill ? styles.tab.pill.disabled : styles.tab.default.disabled)} border-b-0 h-full`}
                    >
                        More
                        <ChevronDownIcon className="w-4 h-4 ml-2" />
                    </Popover.Button>

                    <AnimatePresence initial={false}>
                        {open && !isDisabled && (
                            <Popover.Panel
                                static
                                as={motion.div}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                transition={{ duration: 0.1 }}
                                className="absolute rounded-md border-[1px] p-3 mt-3 shadow-md bg-white dark:bg-gray-800 dark:border-gray-700 flex flex-col gap-1"
                            >
                                {Children.map(children, (child, index) => {
                                    if (index + 1 > childOffsetIndex) {
                                        return isValidElement(child) ? (
                                            <VerticalNavTab close={close} isDisabled={isDisabled} {...child.props}>
                                                {typeof child.props.children === 'function'
                                                    ? child.props.children({ close })
                                                    : child.props.children}
                                            </VerticalNavTab>
                                        ) : null;
                                    }
                                })}
                            </Popover.Panel>
                        )}
                    </AnimatePresence>
                </>
            )}
        </Popover>
    );
}
